import {
  useWSModalCustom,
  WSAlert,
  WSElement,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSSectionToolbar,
  WSSelectOption,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IPayerEngagementListResponse,
  IPayerResponse,
  WorkLogStatus
} from "@wingspanhq/payments/dist/interfaces";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import { Nullable } from "primereact/ts-helpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { toURLQuerySearch } from "../../../shared/hooks/useUrlQuery";
import { useQueryPayerEngagement } from "../../../query/payerEngagements/queries/useQueryPayerEngagement";
import { useQueryPayer } from "../../../query/payers/queries/useQueryPayer";
import { useMutationCreateWorkLog } from "../../../query/workLog/mutations/useMutationCreateWorkLog";
import { useQueryWorkLogs } from "../../../query/workLog/queries/useQueryWorkLogs";
import { SelectPayerEngagement } from "../../Payers/components/SelectPayerEngagement";
import { WorkLogEngagementDetails } from "../components/WorkLogEngagementDetails";
import {
  getPathWorkLogPayeeDetails,
  PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS
} from "../paths";

type Props = {
  defaultPayerId?: string;
  defaultPayerPayeeEngagementId?: string;
} & WSModalCustomProps<void>;

export const ModalCreateWorkLog: React.FC<Props> = ({
  defaultPayerId,
  defaultPayerPayeeEngagementId,
  ...props
}) => {
  const history = useHistory();
  const [selectValue, setSelectValue] =
    useState<Nullable<WSSelectOption>>(null);

  const isPreselected = !!defaultPayerId && !!defaultPayerPayeeEngagementId;

  const { payerId, payerPayeeEngagementId } = useMemo(() => {
    if (!selectValue) {
      // Use preselected values while loading (before selectValue is set)
      if (isPreselected) {
        return {
          payerId: defaultPayerId,
          payerPayeeEngagementId: defaultPayerPayeeEngagementId
        };
      }
      return {};
    }
    const [payerId, engagementId] = selectValue.value.split("/");
    return { payerId, payerPayeeEngagementId: engagementId };
  }, [
    isPreselected,
    defaultPayerId,
    defaultPayerPayeeEngagementId,
    selectValue
  ]);

  const queryPayer = useQueryPayer(payerId!, { enabled: !!payerId });
  const queryEngagement = useQueryPayerEngagement(
    payerId!,
    payerPayeeEngagementId!,
    {
      enabled: !!payerId && !!payerPayeeEngagementId
    }
  );

  // Initialize selectValue when preselected values are provided
  useEffect(() => {
    if (
      isPreselected &&
      queryPayer.data &&
      queryEngagement.data &&
      !selectValue
    ) {
      const payerNames = wsName({
        user: queryPayer.data.user!,
        member: queryPayer.data.member,
        payeeOwnedData: queryPayer.data.payeeOwnedData
      });
      const payerName = payerNames.getResolvedName();
      const engagementName = queryEngagement.data.engagementName;

      setSelectValue({
        value: `${defaultPayerId}/${defaultPayerPayeeEngagementId}`,
        label: `${payerName} • ${engagementName}`,
        labels: [payerNames.email].filter(Boolean) as string[],
        avatar: {
          type: "text",
          text: payerName
        }
      });
    }
  }, [
    isPreselected,
    queryPayer.data,
    queryEngagement.data,
    defaultPayerId,
    defaultPayerPayeeEngagementId,
    selectValue
  ]);

  const queryExistingWorkLogs = useQueryWorkLogs(
    {
      "filter[payerPayeeEngagementId][=]": payerPayeeEngagementId,
      "filter[status][=]": WorkLogStatus.Draft,
      "filter[endDate][exists]": false
    },
    { enabled: !!payerPayeeEngagementId }
  );

  const existingWorkLogs = queryExistingWorkLogs.data || [];

  const [createWorkLog, createWorkLogMeta] = useMutationCreateWorkLog();

  const handleAddNewPayer = useCallback(
    (payer: IPayerResponse, engagements: IPayerEngagementListResponse) => {
      if (engagements.length > 0) {
        const payerEngagement = engagements[0];
        const payerNames = wsName({
          user: payer.user!,
          member: payer.member,
          payeeOwnedData: payer.payeeOwnedData
        });
        const payerName = payerNames.getResolvedName();

        setSelectValue({
          value: `${payer.payerId}/${payerEngagement.payerPayeeEngagementId}`,
          label: `${payerName} • ${payerEngagement.engagementName}`,
          labels: [payerNames.email],
          avatar: {
            type: "text",
            text: payerName
          }
        });
      }
    },
    []
  );

  const handleViewExistingWorkLogs = useCallback(() => {
    const search = toURLQuerySearch({
      filter: { assignmentId: payerPayeeEngagementId }
    });
    history.push(`${PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS}?${search}`);
    props.onClose();
  }, [history, payerPayeeEngagementId, props]);

  return (
    <WSModal
      title="New work log"
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Tertiary",
            label: "Cancel",
            onClick: () => props.onClose()
          },
          {
            kind: "Primary",
            label: "Create new work log",
            onClick: () => {
              if (!payerPayeeEngagementId) return;

              createWorkLog(
                {
                  startDate: new Date(),
                  payerPayeeEngagementId
                },
                {
                  onSuccess: workLog => {
                    history.push(getPathWorkLogPayeeDetails(workLog.workLogId));
                    props.onClose();
                  }
                }
              );
            },
            loading: createWorkLogMeta.isLoading,
            disabled: !payerPayeeEngagementId
          }
        ]
      }}
      divider
      {...props}
    >
      <WSList gap="L">
        <WSElement>
          <WSSectionToolbar title="Client assignment *" mb="XS" />
          <WSText.ParagraphSm color="gray500" mb="M">
            Select the client assignment to log work for
          </WSText.ParagraphSm>
          <SelectPayerEngagement
            autoFocus={!isPreselected}
            value={selectValue}
            onChange={setSelectValue}
            onAddNewPayer={handleAddNewPayer}
            baseRequest={{
              filter: {
                "engagements.isWorkLoggingEnabled": true
              }
            }}
            menuFooterText="Only assignments set up for work log tracking are available to select."
            hideMenuFooterAction
          />
        </WSElement>

        {payerPayeeEngagementId && (
          <WorkLogEngagementDetails
            shimmer={queryPayer.isLoading || queryEngagement.isLoading}
            payer={queryPayer.data}
            payerPayeeEngagement={queryEngagement.data}
          />
        )}

        {existingWorkLogs.length > 0 && (
          <WSAlert
            theme="info"
            icon="info-circle"
            title="Existing work logs found"
            action={{
              text: "View existing work logs",
              onClick: handleViewExistingWorkLogs
            }}
          >
            You have {existingWorkLogs.length} in-progress work log
            {existingWorkLogs.length > 1 ? "s" : ""} for this assignment. Would
            you like to continue working on those, or start a fresh work log?
          </WSAlert>
        )}

        <WSErrorMessage contextKey="WorkLog" error={createWorkLogMeta.error} />
      </WSList>
    </WSModal>
  );
};

export const useModalCreateWorkLog = () => useWSModalCustom(ModalCreateWorkLog);
